import { transformDocument } from '@/pages/UserAccount/Profile';

import { FormDocument } from '@/features/ui/DocumentsData/model/types';

import { OwnerPayload } from '@/shared/lib/helpers/payloadHelpers';
import { SelectOption } from '@/shared/types';

import { ExtractFormType } from '@/modules/iac/pages/services/ExtendedCriminalRecord/lib/constants';
import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { DeliveryType, NamesBeforeFieldName } from '@/lib/consts';

import { DocumentData, FormDataType, UserRequestDraft, UserService } from '@/api/types';

import { AddressPayload, buildUser, getBirthAddress, getRegisteredAddress } from './payloadHelpers';

type GeneratePayloadParams = {
  requestId: UserRequestDraft['id'];
  formData: FormDataType;
  service: UserService;
  serviceFileManager?: ServiceFileManager;
};

export type NamesBeforeValue = Record<
  NamesBeforeFieldName.previousLastName | NamesBeforeFieldName.previousName | NamesBeforeFieldName.previousPatronymic,
  string
>;

export interface UserPayload extends Omit<
  OwnerPayload,
  'birth_place' | 'doc_name' | 'doc_type' | 'serial' | 'issued' | 'auth_dep'
> {
  names_before?: NamesBeforeValue[];
  doc_type?: string;
  doc_name?: string;
  serial: Nullable<string>;
  issued: Nullable<string>;
  auth_dep: Nullable<string>;
}

interface Payload {
  requestId: UserRequestDraft['id'];
  service: {
    id: string;
    name: string;
    code: string;
  };
  user: UserPayload;
  birth_address: AddressPayload;
  registered_address?: AddressPayload;
  purpose_id: string;
  extract_type: string;
  extract_form_type: string;
  delivery_type?: string;
  address_delivery?: {
    index: string;
    warehouse: string;
  };
  has_apostille?: boolean;
  documents?: DocumentData[];
}

export const generatePayload = async ({
  requestId,
  formData,
  service,
  serviceFileManager,
}: GeneratePayloadParams): Promise<{
  commonPayload: Payload;
  signedPayload: Payload & Partial<ReturnType<ServiceFileManager['getSignature']>>;
}> => {
  const passportDocumentType = formData.personPassportDocument as Nullable<FormDocument['type']>;
  const passportDocCode = passportDocumentType?.code;
  const documents = formData.documents as Nullable<Record<string, FormDocument>>;
  const passportDocumentData = passportDocCode ? documents?.[passportDocCode] : undefined;
  const passport = {
    type: passportDocumentType,
    info: passportDocumentData?.info,
  };

  const commonPayload: Payload = {
    requestId,
    service: {
      id: service.id,
      name: service.name,
      code: service.code,
    },
    user: buildUser(formData, passport as Nullable<FormDocument>),
    birth_address: getBirthAddress(formData as ExtendedCriminalRecordExtractSchema),
    extract_type: (formData.extractType as Nullable<SelectOption>)?.id ?? '',
    purpose_id: (formData.requestPurpose as Nullable<SelectOption>)?.id ?? '',
    extract_form_type: formData.extractFormType as string,
    documents: transformDocument(
      { passport: passportDocumentData as any },
      {
        passportDocTypeCode: passportDocumentType?.code,
        registeredDocTypeCode: undefined,
        actualDocTypeCode: undefined,
      },
    ),
  };

  if (formData.extractFormType === ExtractFormType.PAPER) {
    const deliveryType = (formData.deliveryType as Nullable<SelectOption>)?.id ?? '';
    commonPayload.delivery_type = deliveryType;
    commonPayload.has_apostille = !!formData.hasApostille as boolean;

    if ((formData.deliveryType as Nullable<SelectOption>)?.id !== DeliveryType.PERSONAL) {
      const warehouse: Nullable<SelectOption> = formData.warehouse as Nullable<SelectOption>;

      commonPayload.address_delivery = {
        index: formData.indexNumber as string,
        warehouse:
          deliveryType === DeliveryType.COURIER
            ? `${warehouse?.id}${formData.building ? `, корпус ${formData.building}` : ''}${formData.apartment ? `, квартира ${formData.apartment}` : ''}`
            : (warehouse?.label ?? ''),
      };
    }
  } else {
    commonPayload.registered_address = getRegisteredAddress(formData as ExtendedCriminalRecordExtractSchema);
  }

  const signedPayload = {
    ...commonPayload,
    ...(serviceFileManager?.getSignature() ?? {}),
  };

  return {
    commonPayload,
    signedPayload,
  };
};
